import {useCallback, useEffect} from "react";
import {V2PoolInfo, poolUtils} from "@tinymanorg/tinyman-js-sdk";

import {getAssetId, isValidAssetID} from "../../../../asset/util/assetUtils";
import {algod} from "../../../../core/integrations/algosdk/AlgodManager";
import useAsyncProcess from "../../../../core/network/async-process/useAsyncProcess";
import useInterval from "../../../../core/util/hook/useInterval";
import {PREFERRED_NETWORK} from "../../../../core/constants";
import {useSwapFormState} from "../../context/SwapFormContext";

const SWAP_FORM_POOLS_FOR_PAIR_FETCH_INTERVAL = 15000;

function useFetchSwapPool() {
  const {assetIn, assetOut} = useSwapFormState();

  const fetchSwapPool = useCallback(async () => {
    if (
      assetIn.asset &&
      isValidAssetID(getAssetId(assetIn.asset)) &&
      assetOut.asset &&
      isValidAssetID(getAssetId(assetOut.asset))
    ) {
      const pool = await poolUtils.v2.getPoolInfo({
        asset1ID: getAssetId(assetIn.asset),
        asset2ID: getAssetId(assetOut.asset),
        client: algod.client,
        network: PREFERRED_NETWORK
      });

      return pool;
    }

    throw new Error("Invalid asset IDs provided to fetch pool details for the swap");
  }, [assetIn.asset, assetOut.asset]);

  return fetchSwapPool;
}

function useSwapPool() {
  const {
    state: asyncState,
    runAsyncProcess,
    setState: setAsyncState
  } = useAsyncProcess<V2PoolInfo | null>();
  const {isReady} = useSwapFormState();
  const fetchSwapPool = useFetchSwapPool();

  useEffect(() => {
    if (isReady) {
      runAsyncProcess(fetchSwapPool());
    }
  }, [runAsyncProcess, fetchSwapPool, isReady]);

  useInterval(() => {
    fetchSwapPool().then((data) => {
      setAsyncState({
        data,
        isRequestFetched: true,
        isRequestPending: false,
        error: null
      });
    });
  }, SWAP_FORM_POOLS_FOR_PAIR_FETCH_INTERVAL);

  return asyncState;
}

export {useFetchSwapPool, useSwapPool};
